import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { profileService } from "../services/profileService";
import { drillsService } from "../services/drillsService";
import { objectionsService } from "../services/objectionsService";
import { getLeaderboard, getUserLeaderboardStats, getUserRankHistory } from "../services/leaderboardService";

/**
 * Aggregated dashboard data with caching and prioritized loading.
 * - Loads critical data (profile + drill) first
 * - Defers non-critical data (history, leaderboard, objections) to avoid initial load bottleneck
 * - Keeps previous data visible when navigating back and forth
 * - Avoids refetch-on-focus to prevent spinners on tab return
 */
export function useDashboardData({ userId, level = 1, profile, tenantId }) {
  // Track when we should load deferred queries
  const [loadDeferred, setLoadDeferred] = useState(false);

  // Guard: Don't run anything if userId is missing
  const hasValidUserId = Boolean(userId && userId !== 'undefined' && userId !== 'null');

  // CRITICAL: Profile + Today's Drill (load immediately)
  const profileQuery = useQuery({
    queryKey: ["dashboard", "profile", userId],
    queryFn: () => profileService.getUserProfile(),
    enabled: hasValidUserId,
    staleTime: 5 * 60 * 1000,
  });

  const todaysDrillQuery = useQuery({
    queryKey: ["dashboard", "todaysDrill", userId, level, tenantId],
    queryFn: async () => {
      const prof = profileQuery.data || profile;
      const drill = await drillsService.getTodaysDrillByLevel(prof?.level || level || 1, prof, tenantId);
      if (!drill) return null;
      return {
        id: drill?.id,
        title: drill?.title,
        preview: (drill?.promptText || "").slice(0, 200) + "...",
        duration: "8 min",
        difficulty: drill?.difficultyLevel === 1 ? "Beginner" : drill?.difficultyLevel === 2 ? "Intermediate" : drill?.difficultyLevel === 3 ? "Advanced" : "Intermediate",
        industry: drill?.industry,
        isCompleted: false,
      };
    },
    enabled: hasValidUserId,
    staleTime: 5 * 60 * 1000,
  });

  // Once critical data loads, unlock deferred queries
  useEffect(() => {
    if (profileQuery.data && todaysDrillQuery.data !== undefined) {
      setLoadDeferred(true);
    }
  }, [profileQuery.data, todaysDrillQuery.data]);

  // DEFERRED: History, objections, leaderboard (load after critical data)
  const drillHistoryQuery = useQuery({
    queryKey: ["dashboard", "drillHistory", userId],
    queryFn: async () => {
      try {
        const history = await drillsService.getUserDrillHistory(100);
        if (!history?.length) return { history: [], skills: [] };

        const mappedHistory = history.map((record) => ({
          ...record.drill,
          lumoScore: record.score || 0,
          completedDate: record.completedAt,
          createdAt: record.createdAt,
          scores: record.scores || {},
        }));

        const recent = mappedHistory.slice(0, 10);
        const skillScores = { clarity: [], confidence: [], control: [], close: [] };
        recent.forEach((d) => {
          if (!d.scores) return;
          if (d.scores.clarity) skillScores.clarity.push(d.scores.clarity);
          if (d.scores.confidence) skillScores.confidence.push(d.scores.confidence);
          if (d.scores.control) skillScores.control.push(d.scores.control);
          if (d.scores.close) skillScores.close.push(d.scores.close);
        });

        const toSkill = (scores, name, id) => {
          if (!scores.length) return null;
          const avg = Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
          const half = Math.floor(scores.length / 2);
          const firstAvg = half ? scores.slice(0, half).reduce((a, b) => a + b, 0) / half : 0;
          const second = scores.length - half;
          const secondAvg = second ? scores.slice(half).reduce((a, b) => a + b, 0) / second : 0;
          const trend = Math.round(secondAvg - firstAvg);
          return { id, name, score: avg, trend };
        };

        const skills = [
          toSkill(skillScores.clarity, "Clarity", 1),
          toSkill(skillScores.confidence, "Confidence", 2),
          toSkill(skillScores.control, "Control", 3),
          toSkill(skillScores.close, "Close", 4),
        ].filter(Boolean);

        return { history: mappedHistory, skills };
      } catch (err) {
        console.warn("Could not fetch drill history (deferred):", err?.message || err);
        return { history: [], skills: [] };
      }
    },
    enabled: hasValidUserId && loadDeferred,
    staleTime: 5 * 60 * 1000,
  });

  const objectionAttemptsQuery = useQuery({
    queryKey: ["dashboard", "objections", userId],
    queryFn: async () => {
      try {
        const attempts = await objectionsService.getUserAttempts(userId);
        return (attempts || []).map((attempt) => ({
          objectionCategory: attempt.objection_category || "General",
          lumoScore: attempt.lumo_score || 0,
          completedDate: attempt.created_at,
          createdAt: attempt.created_at,
        }));
      } catch (err) {
        console.warn("Could not fetch objection attempts (deferred):", err?.message || err);
        return [];
      }
    },
    enabled: hasValidUserId && loadDeferred,
    staleTime: 5 * 60 * 1000,
  });

  const leaderboardQuery = useQuery({
    queryKey: ["dashboard", "leaderboard", userId],
    queryFn: async () => {
      try {
        const [leaderboard, userStats, rankHistory] = await Promise.all([
          getLeaderboard("global", "total_xp", { limit: 5 }),
          getUserLeaderboardStats(userId),
          getUserRankHistory(userId, 7),
        ]);

        if (userStats && rankHistory?.length >= 2) {
          const latest = rankHistory[rankHistory.length - 1];
          const previous = rankHistory[rankHistory.length - 2];
          userStats.rank_change = (previous.global_rank || 0) - (latest.global_rank || 0);
        }

        return { leaderboard: leaderboard || [], userStats: userStats || null };
      } catch (err) {
        console.warn("Could not fetch leaderboard (deferred):", err?.message || err);
        return { leaderboard: [], userStats: null };
      }
    },
    enabled: hasValidUserId && loadDeferred,
    staleTime: 10 * 60 * 1000,
  });

  // Only show loading spinner if CRITICAL data is missing
  const isCriticalLoading = profileQuery.isLoading || todaysDrillQuery.isLoading;
  
  // Show background refetch indicator only for deferred data
  const isDeferredFetching = 
    drillHistoryQuery.isFetching || 
    objectionAttemptsQuery.isFetching || 
    leaderboardQuery.isFetching;

  return {
    profile: profileQuery.data,
    todaysDrill: todaysDrillQuery.data,
    drillHistory: drillHistoryQuery.data?.history || [],
    skillsProgress: drillHistoryQuery.data?.skills || [],
    objectionAttempts: objectionAttemptsQuery.data || [],
    leaderboard: leaderboardQuery.data?.leaderboard || [],
    userLeaderboardStats: leaderboardQuery.data?.userStats || null,
    // Only block UI for critical data load
    isLoading: isCriticalLoading,
    // Background fetch indicator for deferred data
    isFetching: isDeferredFetching,
    refetchAll: async () => {
      await Promise.all([
        profileQuery.refetch(),
        todaysDrillQuery.refetch(),
        drillHistoryQuery.refetch(),
        objectionAttemptsQuery.refetch(),
        leaderboardQuery.refetch(),
      ]);
    },
  };
}
